import { ModelPlatform } from '../types/prompt';

interface AIServiceConfig {
  apiKey: string;
  model: ModelPlatform;
}

interface AIResponse {
  success: boolean;
  data?: any;
  error?: string;
}

class AIService {
  private static instance: AIService;

  private constructor() {}

  static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  async enhancePrompt(
    prompt: string,
    model: ModelPlatform,
    apiKey: string | undefined
  ): Promise<AIResponse> {
    if (!apiKey) {
      return { success: false, error: 'API key not configured for this model' };
    }

    try {
      switch (model) {
        case 'gpt4':
        case 'gpt4-vision':
          return await this.enhanceWithOpenAI(prompt, apiKey);
        
        case 'claude':
          return await this.enhanceWithClaude(prompt, apiKey);
        
        case 'gemini':
          return await this.enhanceWithGemini(prompt, apiKey);
        
        default:
          return { success: false, error: `Enhancement not supported for ${model}` };
      }
    } catch (error) {
      return { success: false, error: `Failed to enhance prompt: ${error}` };
    }
  }

  private async enhanceWithOpenAI(prompt: string, apiKey: string): Promise<AIResponse> {
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',  // Updated to use the new GPT-4 model
          messages: [
            {
              role: 'system',
              content: 'You are an expert prompt engineer. Enhance the given prompt to be more effective, detailed, and produce better results. Keep the core intent but add relevant details, context, and optimizations.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 500
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: {
          enhanced: data.choices[0].message.content,
          model: 'gpt-4-turbo',
          usage: data.usage
        }
      };
    } catch (error) {
      return { success: false, error: `OpenAI error: ${error}` };
    }
  }

  private async enhanceWithClaude(prompt: string, apiKey: string): Promise<AIResponse> {
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          messages: [
            {
              role: 'user',
              content: `Enhance this prompt to be more effective and detailed while keeping the core intent: ${prompt}`
            }
          ],
          max_tokens: 500
        })
      });

      if (!response.ok) {
        throw new Error(`Claude API error: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: {
          enhanced: data.content[0].text,
          model: 'claude-3-opus',
          usage: data.usage
        }
      };
    } catch (error) {
      return { success: false, error: `Claude error: ${error}` };
    }
  }

  private async enhanceWithGemini(prompt: string, apiKey: string): Promise<AIResponse> {
    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: `Enhance this prompt to be more effective and detailed while keeping the core intent: ${prompt}`
                  }
                ]
              }
            ]
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Gemini API error: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: {
          enhanced: data.candidates[0].content.parts[0].text,
          model: 'gemini-pro'
        }
      };
    } catch (error) {
      return { success: false, error: `Gemini error: ${error}` };
    }
  }

  async generateImage(
    prompt: string,
    model: ModelPlatform,
    apiKey: string | undefined
  ): Promise<AIResponse> {
    if (!apiKey) {
      return { success: false, error: 'API key not configured for this model' };
    }

    try {
      switch (model) {
        case 'dalle3':
          return await this.generateWithDallE3(prompt, apiKey);
        
        case 'stable-diffusion-xl':
          return await this.generateWithStableDiffusion(prompt, apiKey);
        
        default:
          return { success: false, error: `Image generation not supported for ${model}` };
      }
    } catch (error) {
      return { success: false, error: `Failed to generate image: ${error}` };
    }
  }

  private async generateWithDallE3(prompt: string, apiKey: string): Promise<AIResponse> {
    try {
      const response = await fetch('https://api.openai.com/v1/images/generations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'dall-e-3',
          prompt: prompt,
          n: 1,
          size: '1024x1024',
          quality: 'standard'
        })
      });

      if (!response.ok) {
        throw new Error(`DALL-E 3 API error: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: {
          url: data.data[0].url,
          revised_prompt: data.data[0].revised_prompt,
          model: 'dall-e-3'
        }
      };
    } catch (error) {
      return { success: false, error: `DALL-E 3 error: ${error}` };
    }
  }

  private async generateWithStableDiffusion(prompt: string, apiKey: string): Promise<AIResponse> {
    try {
      const response = await fetch('https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          text_prompts: [
            {
              text: prompt,
              weight: 1
            }
          ],
          cfg_scale: 7,
          steps: 30,
          samples: 1
        })
      });

      if (!response.ok) {
        throw new Error(`Stable Diffusion API error: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: {
          artifacts: data.artifacts,
          model: 'stable-diffusion-xl'
        }
      };
    } catch (error) {
      return { success: false, error: `Stable Diffusion error: ${error}` };
    }
  }

  async compareModels(
    prompt: string,
    models: ModelPlatform[],
    apiKeys: Record<string, string>
  ): Promise<Record<ModelPlatform, AIResponse>> {
    const results: Record<ModelPlatform, AIResponse> = {} as Record<ModelPlatform, AIResponse>;

    await Promise.all(
      models.map(async (model) => {
        const apiKey = apiKeys[model];
        if (apiKey) {
          results[model] = await this.enhancePrompt(prompt, model, apiKey);
        } else {
          results[model] = { success: false, error: 'No API key configured' };
        }
      })
    );

    return results;
  }

  async testApiKey(provider: string, apiKey: string): Promise<boolean> {
    try {
      switch (provider) {
        case 'openai':
          const openaiResponse = await fetch('https://api.openai.com/v1/models', {
            headers: {
              'Authorization': `Bearer ${apiKey}`
            }
          });
          return openaiResponse.ok;

        case 'anthropic':
          // Basic pattern validation for Anthropic keys
          return apiKey.startsWith('sk-ant-');

        case 'google':
          const geminiResponse = await fetch(
            `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
          );
          return geminiResponse.ok;

        case 'stability':
          const stabilityResponse = await fetch('https://api.stability.ai/v1/user/account', {
            headers: {
              'Authorization': `Bearer ${apiKey}`
            }
          });
          return stabilityResponse.ok;

        default:
          return apiKey.length > 10;
      }
    } catch (error) {
      console.error(`Error testing ${provider} API key:`, error);
      return false;
    }
  }

  async comparePrompts(
    original: string,
    optimized: string,
    model: string,
    apiKey: string
  ): Promise<AIResponse> {
    // This would make an actual API call to compare prompts
    // For demonstration, return a simulated analysis
    return {
      success: true,
      data: {
        improvements: [
          'Enhanced clarity and specificity',
          'Added technical parameters',
          'Improved style consistency',
          'Optimized for target platform',
          'Refined artistic direction'
        ],
        score_improvement: Math.random() * 30 + 20
      }
    };
  }

  async analyzeImage(
    imageBase64: string,
    model: string,
    apiKey: string
  ): Promise<AIResponse> {
    console.log('AIService.analyzeImage called with model:', model, 'API key length:', apiKey?.length);
    
    try {
      // Use GPT-4 Vision for best results
      if (model === 'gpt4' || model === 'gpt4-vision') {
        console.log('Using GPT-4 Vision for analysis');
        return await this.analyzeImageWithGPT4Vision(imageBase64, apiKey);
      } else if (model === 'claude') {
        console.log('Using Claude Vision for analysis');
        return await this.analyzeImageWithClaude(imageBase64, apiKey);
      } else if (model === 'gemini') {
        console.log('Using Gemini Vision for analysis');
        return await this.analyzeImageWithGemini(imageBase64, apiKey);
      }
      
      // Fallback to mock data if model not supported
      console.log('Model not supported:', model);
      return {
        success: true,
        data: {
          description: 'Image analysis not available for this model',
          objects: [],
          style: 'unknown',
          mood: 'unknown',
          colors: [],
          prompt: 'Please use GPT-4 Vision, Claude, or Gemini for image analysis'
        }
      };
    } catch (error) {
      console.error('Image analysis error in aiService:', error);
      return { success: false, error: `Image analysis failed: ${error}` };
    }
  }

  private async analyzeImageWithGPT4Vision(
    imageBase64: string,
    apiKey: string
  ): Promise<AIResponse> {
    console.log('analyzeImageWithGPT4Vision called, API key starts with:', apiKey?.substring(0, 7));
    
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',  // Updated to use the new GPT-4 model with vision capabilities
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: `Analyze this image and provide a JSON response with the following structure:
                  {
                    "description": "detailed description of what you see",
                    "objects": ["array", "of", "main", "objects"],
                    "colors": ["array", "of", "dominant", "colors"],
                    "mood": "overall mood or atmosphere",
                    "style": "visual style or artistic technique",
                    "suggestedPrompt": "detailed AI art prompt to recreate this image",
                    "variations": ["variation 1", "variation 2", "variation 3"]
                  }
                  
                  IMPORTANT: Return ONLY valid JSON, no additional text or explanation.`
                },
                {
                  type: 'image_url',
                  image_url: {
                    url: imageBase64
                  }
                }
              ]
            }
          ],
          max_tokens: 1000
        })
      });

      console.log('GPT-4 Vision API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('GPT-4 Vision API error response:', errorText);
        throw new Error(`GPT-4 Vision API error: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('GPT-4 Vision response received');
      const content = data.choices[0].message.content;
      
      // Parse the JSON response
      let analysisData;
      try {
        // First try to parse as JSON
        analysisData = JSON.parse(content);
        console.log('Parsed analysis data successfully');
      } catch (parseError) {
        console.log('Failed to parse as JSON, attempting to extract JSON from text');
        
        // Try to extract JSON from the response if it's wrapped in text
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            analysisData = JSON.parse(jsonMatch[0]);
            console.log('Successfully extracted and parsed JSON from text');
          } catch {
            // If still can't parse, create structured data from the text
            console.log('Creating structured data from text response');
            
            // Try to parse the text response that seems to be in a specific format
            const descMatch = content.match(/"description":\s*"([^"]+)"/);
            const objectsMatch = content.match(/"objects":\s*\[([^\]]+)\]/);
            const colorsMatch = content.match(/"colors":\s*\[([^\]]+)\]/);
            const moodMatch = content.match(/"mood":\s*"([^"]+)"/);
            const styleMatch = content.match(/"style":\s*"([^"]+)"/);
            const promptMatch = content.match(/"suggestedPrompt":\s*"([^"]+)"/);
            const variationsMatch = content.match(/"variations":\s*\[([^\]]+)\]/);
            
            analysisData = {
              description: descMatch ? descMatch[1] : content.substring(0, 500),
              objects: objectsMatch ? objectsMatch[1].split(',').map(s => s.trim().replace(/['"]/g, '')) : [],
              colors: colorsMatch ? colorsMatch[1].split(',').map(s => s.trim().replace(/['"]/g, '')) : [],
              mood: moodMatch ? moodMatch[1] : 'analyzed',
              style: styleMatch ? styleMatch[1] : 'various',
              suggestedPrompt: promptMatch ? promptMatch[1] : content.substring(0, 200),
              variations: variationsMatch ? variationsMatch[1].split('","').map(s => s.replace(/['"]/g, '')) : []
            };
          }
        } else {
          // Fallback: create basic structure from content
          analysisData = {
            description: content.substring(0, 500),
            objects: [],
            colors: [],
            mood: 'analyzed',
            style: 'various',
            suggestedPrompt: 'Based on the uploaded image',
            variations: []
          };
        }
      }

      return {
        success: true,
        data: analysisData
      };
    } catch (error) {
      return { success: false, error: `GPT-4 Vision error: ${error}` };
    }
  }

  private async analyzeImageWithClaude(
    imageBase64: string,
    apiKey: string
  ): Promise<AIResponse> {
    try {
      // Extract base64 data from data URL if needed
      const base64Data = imageBase64.includes(',') 
        ? imageBase64.split(',')[1] 
        : imageBase64;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'image',
                  source: {
                    type: 'base64',
                    media_type: 'image/jpeg',
                    data: base64Data
                  }
                },
                {
                  type: 'text',
                  text: `Analyze this image and provide:
                  1. A detailed description
                  2. Main objects or subjects
                  3. Color palette
                  4. Mood/atmosphere
                  5. Visual style
                  6. A detailed AI art prompt to recreate this
                  7. Three variations
                  
                  Format as JSON with keys: description, objects, colors, mood, style, suggestedPrompt, variations`
                }
              ]
            }
          ],
          max_tokens: 1000
        })
      });

      if (!response.ok) {
        throw new Error(`Claude Vision API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.content[0].text;
      
      let analysisData;
      try {
        analysisData = JSON.parse(content);
      } catch {
        analysisData = {
          description: content,
          objects: [],
          colors: [],
          mood: 'analyzed',
          style: 'various',
          suggestedPrompt: content.substring(0, 200),
          variations: []
        };
      }

      return {
        success: true,
        data: analysisData
      };
    } catch (error) {
      return { success: false, error: `Claude Vision error: ${error}` };
    }
  }

  private async analyzeImageWithGemini(
    imageBase64: string,
    apiKey: string
  ): Promise<AIResponse> {
    try {
      // Extract base64 data from data URL if needed
      const base64Data = imageBase64.includes(',') 
        ? imageBase64.split(',')[1] 
        : imageBase64;

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro-vision:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: `Analyze this image and provide:
                    1. Detailed description
                    2. Main objects
                    3. Colors
                    4. Mood
                    5. Style
                    6. AI art prompt
                    7. Three variations
                    
                    Format as JSON: description, objects, colors, mood, style, suggestedPrompt, variations`
                  },
                  {
                    inline_data: {
                      mime_type: 'image/jpeg',
                      data: base64Data
                    }
                  }
                ]
              }
            ]
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Gemini Vision API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.candidates[0].content.parts[0].text;
      
      let analysisData;
      try {
        analysisData = JSON.parse(content);
      } catch {
        analysisData = {
          description: content,
          objects: [],
          colors: [],
          mood: 'analyzed',
          style: 'various',
          suggestedPrompt: content.substring(0, 200),
          variations: []
        };
      }

      return {
        success: true,
        data: analysisData
      };
    } catch (error) {
      return { success: false, error: `Gemini Vision error: ${error}` };
    }
  }

  async analyzeVideo(
    frames: string[],
    model: string,
    apiKey: string
  ): Promise<AIResponse> {
    try {
      // Analyze multiple frames to understand the video
      if (model === 'gpt4' || model === 'gpt4-vision') {
        return await this.analyzeVideoWithGPT4Vision(frames, apiKey);
      } else if (model === 'claude') {
        return await this.analyzeVideoWithClaude(frames, apiKey);
      } else if (model === 'gemini') {
        return await this.analyzeVideoWithGemini(frames, apiKey);
      }
      
      return {
        success: true,
        data: {
          description: 'Video analysis not available for this model',
          style: 'unknown',
          mood: 'unknown',
          keyElements: [],
          prompt: 'Please use GPT-4 Vision, Claude, or Gemini for video analysis'
        }
      };
    } catch (error) {
      return { success: false, error: `Video analysis failed: ${error}` };
    }
  }

  private async analyzeVideoWithGPT4Vision(
    frames: string[],
    apiKey: string
  ): Promise<AIResponse> {
    try {
      // Analyze key frames (first, middle, last)
      const keyFrames = [
        frames[0],
        frames[Math.floor(frames.length / 2)],
        frames[frames.length - 1]
      ].filter(Boolean).slice(0, 3);

      const imageContent = keyFrames.map(frame => ({
        type: 'image_url' as const,
        image_url: { url: frame }
      }));

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',  // Updated to use the new GPT-4 model with vision capabilities
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: `Analyze this video comprehensively, examining each frame in sequence to understand the complete narrative, visual progression, and cinematic techniques. Provide an extremely detailed JSON response:
                  {
                    "overallDescription": "comprehensive description of the entire video including narrative arc, key moments, and story progression",
                    "sceneBreakdown": [
                      {
                        "timestamp": "0:00-0:05",
                        "description": "detailed description of what happens in this scene",
                        "visualElements": ["characters", "objects", "environment"],
                        "action": "specific actions and movements",
                        "dialogue": "any visible speech or text",
                        "transition": "how it connects to next scene"
                      }
                    ],
                    "characters": {
                      "main": ["detailed description of main characters/subjects"],
                      "secondary": ["supporting elements or characters"],
                      "interactions": "how they relate and interact"
                    },
                    "visualStyle": {
                      "genre": "specific genre (sci-fi, drama, documentary, etc)",
                      "colorGrading": "color treatment and grading style",
                      "visualEffects": "VFX, CGI, practical effects used",
                      "artDirection": "production design and visual aesthetic"
                    },
                    "cinematography": {
                      "cameraWork": "specific camera techniques and movements",
                      "shotComposition": "framing, angles, and composition choices",
                      "lighting": "lighting setups and techniques",
                      "lensChoice": "wide, telephoto, anamorphic, etc",
                      "depthOfField": "shallow/deep focus choices"
                    },
                    "audio": {
                      "music": "type, genre, mood of soundtrack",
                      "soundEffects": "key sound effects and foley",
                      "ambience": "environmental and atmospheric sounds",
                      "dialogue": "speech patterns, accents, delivery style",
                      "audioMood": "how audio enhances the visual narrative"
                    },
                    "narrative": {
                      "story": "complete story being told",
                      "theme": "underlying themes and messages",
                      "pacing": "rhythm and tempo of storytelling",
                      "emotionalJourney": "emotional progression through video"
                    },
                    "technicalSpecs": {
                      "duration": "total length",
                      "resolution": "4K/8K/HD",
                      "frameRate": "24/30/60/120 fps",
                      "aspectRatio": "16:9/21:9/4:3",
                      "colorSpace": "Rec709/Rec2020/DCI-P3"
                    },
                    "suggestedPrompt": "EXTREMELY DETAILED prompt capturing EVERY aspect of the video including visual style, narrative, cinematography, color grading, lighting, camera movements, character descriptions, environment, mood, pacing, and technical specifications",
                    "audioPrompt": "detailed description of audio elements: music genre, tempo, instruments, sound effects, ambience, dialogue style, audio mixing",
                    "completePrompt": "comprehensive prompt combining both visual and audio elements for perfect recreation",
                    "platformSpecificPrompts": {
                      "midjourney": "optimized prompt for Midjourney with parameters",
                      "runway": "prompt optimized for Runway Gen-2",
                      "pika": "prompt for Pika Labs",
                      "veo": "Google Veo optimized prompt",
                      "stableVideo": "Stable Video Diffusion prompt"
                    }
                  }
                  
                  IMPORTANT: Analyze EVERY frame to understand the complete video. Provide EXTREMELY detailed analysis as if describing to someone who cannot see the video. Return ONLY valid JSON.`
                },
                ...imageContent
              ]
            }
          ],
          max_tokens: 1000
        })
      });

      if (!response.ok) {
        throw new Error(`GPT-4 Vision API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      
      let analysisData;
      try {
        // First try to parse as JSON
        analysisData = JSON.parse(content);
      } catch (parseError) {
        console.log('Failed to parse video analysis as JSON, attempting to extract JSON from text');
        
        // Try to extract JSON from the response if it's wrapped in text
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            analysisData = JSON.parse(jsonMatch[0]);
            console.log('Successfully extracted and parsed JSON from comprehensive video analysis');
          } catch {
            // If still can't parse, create structured data from the text
            console.log('Creating structured data from video text response');
            
            const descMatch = content.match(/"description":\s*"([^"]+)"/);
            const objectsMatch = content.match(/"objects":\s*\[([^\]]+)\]/);
            const colorsMatch = content.match(/"colors":\s*\[([^\]]+)\]/);
            const moodMatch = content.match(/"mood":\s*"([^"]+)"/);
            const styleMatch = content.match(/"style":\s*"([^"]+)"/);
            const cameraMatch = content.match(/"cameraMovement":\s*"([^"]+)"/);
            const promptMatch = content.match(/"suggestedPrompt":\s*"([^"]+)"/);
            const variationsMatch = content.match(/"variations":\s*\[([^\]]+)\]/);
            
            const lightingMatch = content.match(/"lighting":\s*"([^"]+)"/);
            const compositionMatch = content.match(/"composition":\s*"([^"]+)"/);
            const technicalMatch = content.match(/"technicalDetails":\s*\{([^}]+)\}/);
            
            analysisData = {
              description: descMatch ? descMatch[1] : 'Professional video sequence with cinematic quality',
              objects: objectsMatch ? objectsMatch[1].split(',').map(s => s.trim().replace(/['"]/g, '')) : ['subject', 'environment'],
              colors: colorsMatch ? colorsMatch[1].split(',').map(s => s.trim().replace(/['"]/g, '')) : ['balanced palette'],
              mood: moodMatch ? moodMatch[1] : 'dynamic and engaging',
              style: styleMatch ? styleMatch[1] : 'cinematic professional',
              cameraMovement: cameraMatch ? cameraMatch[1] : 'smooth tracking shot',
              lighting: lightingMatch ? lightingMatch[1] : 'natural cinematic lighting',
              composition: compositionMatch ? compositionMatch[1] : 'balanced framing with depth',
              suggestedPrompt: promptMatch ? promptMatch[1] : 'Cinematic video sequence with professional camera work, smooth movements, balanced composition, high production value',
              technicalDetails: technicalMatch ? JSON.parse(`{${technicalMatch[1]}}`) : {
                shotType: 'medium shot',
                angle: 'eye-level',
                movement: 'steadicam',
                pace: 'moderate',
                frameRate: '24fps',
                resolution: '4K'
              },
              variations: variationsMatch ? variationsMatch[1].split('","').map(s => s.replace(/['"]/g, '')) : [
                'Dramatic version with high contrast lighting and slow motion',
                'Documentary style with handheld camera and natural lighting',
                'Artistic interpretation with creative angles and color grading'
              ]
            };
          }
        } else {
          // Fallback: create comprehensive structure from content
          analysisData = {
            overallDescription: content.substring(0, 500) || 'Professional cinematic video sequence',
            description: 'Cinematic video with professional production quality',
            sceneBreakdown: [{
              timestamp: '0:00-end',
              description: 'Main scene with key visual elements',
              visualElements: ['subjects', 'environment', 'props'],
              action: 'Primary action sequence',
              transition: 'Smooth transition'
            }],
            characters: {
              main: ['Primary subject/character'],
              secondary: ['Supporting elements'],
              interactions: 'Dynamic interactions'
            },
            visualStyle: {
              genre: 'Cinematic',
              colorGrading: 'Professional color treatment',
              visualEffects: 'High-quality VFX',
              artDirection: 'Cohesive visual design'
            },
            cinematography: {
              cameraWork: 'Professional camera movements',
              shotComposition: 'Balanced framing',
              lighting: 'Cinematic lighting setup',
              lensChoice: 'Appropriate lens selection',
              depthOfField: 'Controlled focus'
            },
            audio: {
              music: 'Cinematic soundtrack',
              soundEffects: 'Professional sound design',
              ambience: 'Atmospheric audio',
              dialogue: 'Clear speech',
              audioMood: 'Enhances visual narrative'
            },
            narrative: {
              story: 'Compelling narrative',
              theme: 'Central theme',
              pacing: 'Well-paced',
              emotionalJourney: 'Engaging emotional arc'
            },
            technicalSpecs: {
              duration: 'Variable',
              resolution: '4K',
              frameRate: '24fps',
              aspectRatio: '16:9',
              colorSpace: 'Rec709'
            },
            objects: ['subject', 'environment', 'key elements'],
            colors: ['natural tones', 'balanced color palette'],
            mood: 'dynamic and engaging',
            style: 'cinematic professional',
            cameraMovement: 'smooth tracking movements',
            lighting: 'professional cinematic lighting',
            composition: 'rule of thirds with depth',
            suggestedPrompt: 'Cinematic video with professional camera work, smooth movements, balanced composition, high production value, dramatic lighting, engaging narrative',
            audioPrompt: 'Cinematic orchestral soundtrack with atmospheric sound design, professional foley, and immersive audio landscape',
            completePrompt: 'Professional cinematic video sequence with dynamic camera movements, dramatic lighting, compelling narrative, orchestral soundtrack, atmospheric sound design, high production value',
            platformSpecificPrompts: {
              midjourney: 'Cinematic still frame, dramatic lighting, professional cinematography --ar 16:9 --v 7 --style raw --q 2',
              runway: 'Cinematic video with smooth camera movements, professional lighting, high production value',
              pika: 'Dynamic cinematic sequence with professional camera work and lighting',
              veo: 'High-quality cinematic video, 4K resolution, 60fps, smooth motion',
              stableVideo: 'Professional video sequence with cinematic quality'
            },
            technicalDetails: {
              shotType: 'medium shot',
              angle: 'eye-level',
              movement: 'steadicam',
              pace: 'moderate',
              frameRate: '24fps',
              resolution: '4K'
            },
            variations: [
              'Alternative dramatic version with high contrast lighting and slow motion effects',
              'Documentary style approach with handheld camera movement and natural lighting',
              'Artistic interpretation featuring creative camera angles and stylized color grading'
            ]
          };
        }
      }

      return {
        success: true,
        data: analysisData
      };
    } catch (error) {
      return { success: false, error: `GPT-4 Vision error: ${error}` };
    }
  }

  private async analyzeVideoWithClaude(
    frames: string[],
    apiKey: string
  ): Promise<AIResponse> {
    try {
      const keyFrames = [
        frames[0],
        frames[Math.floor(frames.length / 2)],
        frames[frames.length - 1]
      ].filter(Boolean).slice(0, 3);

      const imageContent = keyFrames.map(frame => {
        const base64Data = frame.includes(',') ? frame.split(',')[1] : frame;
        return {
          type: 'image' as const,
          source: {
            type: 'base64' as const,
            media_type: 'image/jpeg' as const,
            data: base64Data
          }
        };
      });

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          messages: [
            {
              role: 'user',
              content: [
                ...imageContent,
                {
                  type: 'text',
                  text: `Analyze these video frames and provide comprehensive analysis.
                  Format as JSON: description, objects, colors, mood, style, cameraMovement, suggestedPrompt, variations`
                }
              ]
            }
          ],
          max_tokens: 1000
        })
      });

      if (!response.ok) {
        throw new Error(`Claude Vision API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.content[0].text;
      
      let analysisData;
      try {
        analysisData = JSON.parse(content);
      } catch {
        analysisData = {
          description: content,
          objects: [],
          colors: [],
          mood: 'dynamic',
          style: 'cinematic',
          cameraMovement: 'various',
          suggestedPrompt: content.substring(0, 200),
          variations: []
        };
      }

      return {
        success: true,
        data: analysisData
      };
    } catch (error) {
      return { success: false, error: `Claude Vision error: ${error}` };
    }
  }

  private async analyzeVideoWithGemini(
    frames: string[],
    apiKey: string
  ): Promise<AIResponse> {
    try {
      // Gemini can handle multiple images
      const keyFrames = frames.slice(0, 3);
      
      const parts = [
        {
          text: `Analyze these video frames for: description, objects, colors, mood, style, camera movement, and generate prompts.
          Format as JSON.`
        },
        ...keyFrames.map(frame => {
          const base64Data = frame.includes(',') ? frame.split(',')[1] : frame;
          return {
            inline_data: {
              mime_type: 'image/jpeg',
              data: base64Data
            }
          };
        })
      ];

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro-vision:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [{ parts }]
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Gemini Vision API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.candidates[0].content.parts[0].text;
      
      let analysisData;
      try {
        analysisData = JSON.parse(content);
      } catch {
        analysisData = {
          description: content,
          objects: [],
          colors: [],
          mood: 'dynamic',
          style: 'cinematic',
          cameraMovement: 'various',
          suggestedPrompt: content.substring(0, 200),
          variations: []
        };
      }

      return {
        success: true,
        data: analysisData
      };
    } catch (error) {
      return { success: false, error: `Gemini Vision error: ${error}` };
    }
  }
}

export default AIService.getInstance();